import React, { useState } from "react";
import Dropzone, { FileWithPath } from "react-dropzone";
import { CheckCircle, RefreshCcw } from "lucide-react";
import UploadIcon from "@/assets/UploadIcon";
import { cn } from "@/lib/utils";
import { renameDuplicateFilesInPlace } from "../HttpUpload/helpers";
import { Theme, useTheme } from "@/contexts/ThemeContext";

interface SelectLocalFolderFieldProps {
  onSuccess: (files: File[]) => void;
  className?: string;
  iconClassName?: string;
  acceptCsvFiles?: boolean;
  inputClassName?: string;
  isUploadStarted?: boolean;
  isUploadCompleted?: boolean;
  uploadCompleteText?: string;
  acceptedFilesTypes?: string[];
}

const SelectLocalFolderField: React.FC<SelectLocalFolderFieldProps> = ({
  onSuccess,
  className,
  inputClassName,
  isUploadStarted,
  isUploadCompleted,
  uploadCompleteText,
  iconClassName,
  acceptCsvFiles,
  acceptedFilesTypes,
}) => {
  const { theme } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <Dropzone
      onDragEnter={() => setIsDragOver(true)}
      onDragLeave={() => setIsDragOver(false)}
      multiple={!acceptCsvFiles}
      disabled={isUploadStarted}
      onDrop={async (acceptedFiles) => {
        setIsDragOver(false);
        // Removing hidden files
        const files: FileWithPath[] = acceptedFiles.filter(
          (file) => !file.name.startsWith("."),
        );
        onSuccess(renameDuplicateFilesInPlace(files));
      }}
    >
      {({ getRootProps, getInputProps, inputRef }) => {
        // Adding support to select folder to upload
        if (!acceptCsvFiles) {
          inputRef.current?.setAttribute("webkitdirectory", "");
          inputRef.current?.setAttribute("mozdirectory", "");
          inputRef.current?.setAttribute("directory", "");
        }
        return (
          <section className={className}>
            <div
              className={cn(
                "bg-tertiary flex cursor-pointer flex-col items-center rounded-[8px] p-4",
                inputClassName,
              )}
              {...getRootProps()}
            >
              {!isUploadStarted && (
                <>
                  {isDragOver && (
                    <>
                      <div className="bg-primary rounded-[8px] p-2">
                        <UploadIcon
                          className={iconClassName}
                          color={theme === Theme.Light ? "#1d1e1f" : undefined}
                        />
                      </div>
                      <p className="text-center">
                        <span className="text-service-paint">
                          Upload to Hotspring
                        </span>
                      </p>
                    </>
                  )}
                  {acceptCsvFiles ? (
                    <input
                      {...getInputProps()}
                      type="file"
                      multiple={false}
                      accept={
                        acceptedFilesTypes && acceptedFilesTypes.join(",")
                      }
                    />
                  ) : (
                    <input {...getInputProps()} />
                  )}

                  {!isDragOver && (
                    <>
                      <div className="bg-primary rounded-[8px] p-2">
                        <UploadIcon
                          className={iconClassName}
                          color={theme === Theme.Light ? "#1d1e1f" : undefined}
                        />
                      </div>
                      <p className="text-center">
                        <span className="text-service-paint">
                          Click to upload&nbsp;
                        </span>
                        or drag and drop
                      </p>
                    </>
                  )}
                </>
              )}
              {isUploadStarted && !isUploadCompleted && (
                <>
                  <RefreshCcw className="animate-spin" />
                  <b className={cn("add-animated-ellipses")}>
                    Uploading your media
                  </b>
                </>
              )}
              {isUploadStarted && isUploadCompleted && (
                <>
                  <CheckCircle />
                  <b className="">
                    {uploadCompleteText ||
                      " Your upload is complete. Click submit to finish."}
                  </b>
                </>
              )}
            </div>
          </section>
        );
      }}
    </Dropzone>
  );
};

export default SelectLocalFolderField;
